<template>
  <div
    v-if="enabled"
    ref="panelRef"
    class="subscription-debug-panel"
    :class="{ 'subscription-debug-panel--expanded': expanded }"
    :style="positionStyle"
    @mousedown="startDrag"
  >
    <!-- Header -->
    <div class="subscription-debug-panel__header">
      <div class="subscription-debug-panel__title">
        <i
          class="pi pi-database mr-2 lg:mr-0 !text-base !leading-none"
        />
        <span>Subscriptions ({{ totalCount }})</span>
      </div>
      <div class="subscription-debug-panel__actions">
        <button
          class="subscription-debug-panel__action-button"
          title="Refresh Counts"
          aria-label="Refresh counts"
          @click="refreshCounts"
        >
          <i class="pi pi-sync" />
        </button>
        <button
          class="subscription-debug-panel__action-button"
          title="Clear All Subscriptions"
          aria-label="Clear all subscriptions"
          @click="clearAllSubscriptions"
        >
          <i class="pi pi-trash" />
        </button>
        <button
          class="subscription-debug-panel__action-button"
          :title="expanded ? 'Collapse' : 'Expand'"
          :aria-label="expanded ? 'Collapse' : 'Expand'"
          @click="expanded = !expanded"
        >
          <i :class="expanded ? 'pi pi-minus' : 'pi pi-plus'" />
        </button>
      </div>
    </div>

    <!-- Content (only visible when expanded) -->
    <div
      v-if="expanded"
      class="subscription-debug-panel__content"
    >
      <!-- Categories -->
      <div class="subscription-debug-panel__categories">
        <div
          v-for="(count, category) in subscriptionsByCategory"
          :key="category"
          class="subscription-debug-panel__category"
        >
          <div class="subscription-debug-panel__category-name">
            <span class="subscription-debug-panel__badge">{{ count }}</span>
            <span>{{ category }}</span>
          </div>
          <button
            class="subscription-debug-panel__action-button--small"
            title="Clear Category"
            :aria-label="`Clear ${category}`"
            @click="clearCategory(category)"
          >
            <i class="pi pi-times" />
          </button>
        </div>
      </div>

      <!-- Event Log -->
      <div class="subscription-debug-panel__event-log">
        <div class="subscription-debug-panel__section-title">
          <span>Recent Events</span>
          <button
            class="subscription-debug-panel__action-button--small"
            title="Clear Log"
            aria-label="Clear log"
            @click="clearEventLog"
          >
            <i class="pi pi-trash" />
          </button>
        </div>
        <div class="subscription-debug-panel__events">
          <div
            v-for="(event, index) in eventLog"
            :key="index"
            class="subscription-debug-panel__event"
            :class="`subscription-debug-panel__event--${event.type}`"
          >
            <span class="subscription-debug-panel__event-time">{{ formatTime(event.time) }}</span>
            <span class="subscription-debug-panel__event-type">{{ event.type }}</span>
            <span class="subscription-debug-panel__event-category">{{ event.category }}</span>
            <span
              v-if="event.key"
              class="subscription-debug-panel__event-key"
            >{{ event.key }}</span>
          </div>
        </div>
      </div>

      <!-- Subscription Details (new section) -->
      <div
        v-if="activeSubscriptions.length > 0"
        class="subscription-debug-panel__subscription-list"
      >
        <div class="subscription-debug-panel__section-title">
          <span>Active Subscriptions</span>
          <div class="subscription-debug-panel__filter">
            <input
              v-model="subscriptionFilter"
              placeholder="Filter..."
              aria-label="Filter subscriptions"
              class="subscription-debug-panel__filter-input"
            />
          </div>
        </div>

        <div class="subscription-debug-panel__subscription-items">
          <div
            v-for="(subscription, index) in filteredSubscriptions"
            :key="index"
            class="subscription-debug-panel__subscription-item"
          >
            <div class="subscription-debug-panel__subscription-info">
              <div class="subscription-debug-panel__subscription-category">
                {{ subscription.category }}
              </div>
              <div
                v-if="subscription.key"
                class="subscription-debug-panel__subscription-key"
              >
                {{ subscription.key }}
              </div>
              <div class="subscription-debug-panel__subscription-time">
                {{ formatTime(subscription.createdAt) }}
              </div>
            </div>
            <button
              class="subscription-debug-panel__action-button--small"
              title="Remove Subscription"
              aria-label="Remove subscription"
              @click="unregisterSubscription(subscription.unsubscribe)"
            >
              <i class="pi pi-times" />
            </button>
          </div>
        </div>
      </div>

      <!-- Actions -->
      <div class="subscription-debug-panel__actions-row">
        <button
          class="subscription-debug-panel__button"
          :disabled="refreshing"
          @click="refreshSubscriptions"
        >
          <i
            class="pi pi-refresh"
            :class="{ 'pi-spin': refreshing }"
          />
          Force Refresh All
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps<{
  setupCoreSubscriptions?: () => Promise<void>
}>()

// State
const enabled = ref(false)
const expanded = ref(false)
const position = ref({ x: 10, y: 10 })
const dragging = ref(false)
const dragOffset = ref({ x: 0, y: 0 })
const panelRef = ref<HTMLElement | null>(null)
const refreshing = ref(false)
const subscriptionFilter = ref('')

// Track subscription categories and counts
const subscriptionsByCategory = ref<Record<string, number>>({})

// Event log
const eventLog = ref<Array<{
  type: 'register' | 'unregister' | 'clear' | 'refresh'
  category: string
  key?: string
  time: Date
}>>([])

// Active subscriptions
const activeSubscriptions = ref<Array<{
  unsubscribe: () => void
  category: string
  key?: string
  createdAt: Date
  // eslint-disable-next-line indent
}>>([])

// Maximum number of events to keep
const MAX_EVENT_LOG = 50

// Get subscriptions utility
const {
  getActiveSubscriptionCount,
  clearAllSubscriptions: clearAll,
  clearSubscriptionsByCategory,
  getAllSubscriptionCategories,
  unregisterSubscription: unregisterSub,
  getActiveSubscriptions, // Add the new method
} = useSubscriptions()

// Use core subscriptions manager if needed
const coreSubscriptions = useCoreSubscriptions()

// Computed position style
const positionStyle = computed(() => ({
  left: `${position.value.x}px`,
  top: `${position.value.y}px`,
}))

// Compute total subscription count
const totalCount = computed(() => {
  return getActiveSubscriptionCount()
})

// Filter subscriptions based on search
const filteredSubscriptions = computed(() => {
  if (!subscriptionFilter.value) return activeSubscriptions.value

  const filter = subscriptionFilter.value.toLowerCase()
  return activeSubscriptions.value.filter(sub =>
    sub.category.toLowerCase().includes(filter)
    || (sub.key && sub.key.toLowerCase().includes(filter)),
  )
})

// Format time for event log
const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

// Add these variables for polling
const refreshIntervalId = ref<number | null>(null)
const REFRESH_INTERVAL = 1000 // 1 second interval

// Get detailed subscription information
const getSubscriptionDetails = () => {
  // Now we can use the real getActiveSubscriptions method
  activeSubscriptions.value = getActiveSubscriptions()
}

// Refresh subscription counts
const refreshCounts = async () => {
  refreshing.value = true

  try {
    // Clear existing counts
    subscriptionsByCategory.value = {}

    // Get all unique subscription categories
    const allCategories = getAllSubscriptionCategories()

    // Count each subscription category
    for (const category of allCategories) {
      const count = getActiveSubscriptionCount(category)
      if (count > 0) {
        subscriptionsByCategory.value[category] = count
      }
    }

    // Get total count directly from the subscription manager
    const totalActiveCount = getActiveSubscriptionCount()

    // Add "other" category if needed to account for any uncategorized subscriptions
    const totalCategorizedCount = Object.values(subscriptionsByCategory.value)
      .reduce((sum, count) => sum + count, 0)

    if (totalActiveCount > totalCategorizedCount) {
      subscriptionsByCategory.value['other'] = totalActiveCount - totalCategorizedCount
    }

    // Get detailed subscription information
    getSubscriptionDetails()
  }
  finally {
    refreshing.value = false
  }
}

// Clear a specific subscription category
const clearCategory = (category: string) => {
  clearSubscriptionsByCategory(category)

  // Add event to log
  addEvent('clear', category)

  // Refresh the counts
  refreshCounts()
}

// Clear all subscriptions
const clearAllSubscriptions = () => {
  clearAll()

  // Add event to log
  addEvent('clear', 'all')

  // Refresh the counts
  refreshCounts()
}

// Unregister a specific subscription
const unregisterSubscription = (unsubscribe: () => void) => {
  // Get the subscription's category before unregistering
  const subscription = activeSubscriptions.value.find(s => s.unsubscribe === unsubscribe)
  const category = subscription?.category || 'unknown'
  const key = subscription?.key

  // Unregister the subscription
  unregisterSub(unsubscribe)

  // Add event to log
  addEvent('unregister', category, key)

  // Refresh the counts
  refreshCounts()
}

// Force refresh all subscriptions
const refreshSubscriptions = async () => {
  refreshing.value = true
  try {
    // First clear all existing subscriptions
    clearAll()

    // Use coreSubscriptions if available
    if (coreSubscriptions) {
      // First reinitialize the auth subscription
      const setupAuthSubscription = coreSubscriptions.setupAuthSubscription
      if (typeof setupAuthSubscription === 'function') {
        setupAuthSubscription()
      }

      // Then set up core subscriptions
      await coreSubscriptions.setupCoreSubscriptions()
      addEvent('refresh', 'all')
    }
    else if (typeof props.setupCoreSubscriptions === 'function') {
      await props.setupCoreSubscriptions()
      addEvent('refresh', 'all')
    }
    else {
      console.warn('[Debug] setupCoreSubscriptions function not available')
    }
  }
  catch (error) {
    console.error('[Debug] Error refreshing subscriptions:', error)
  }
  finally {
    refreshing.value = false

    // Update counts after refresh
    refreshCounts()
  }
}

// Clear the event log
const clearEventLog = () => {
  eventLog.value = []
}

// Add an event to the log
const addEvent = (type: 'register' | 'unregister' | 'clear' | 'refresh', category: string, key?: string) => {
  eventLog.value.unshift({
    type,
    category,
    key,
    time: new Date(),
  })

  // Trim event log if needed
  if (eventLog.value.length > MAX_EVENT_LOG) {
    eventLog.value = eventLog.value.slice(0, MAX_EVENT_LOG)
  }
}

// Drag and drop functionality
const startDrag = (event: MouseEvent) => {
  // Only allow dragging from the header
  if (!(event.target as HTMLElement).closest('.subscription-debug-panel__header')) {
    return
  }

  dragging.value = true

  if (panelRef.value) {
    const rect = panelRef.value.getBoundingClientRect()
    dragOffset.value = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top,
    }
  }

  document.addEventListener('mousemove', handleDrag)
  document.addEventListener('mouseup', stopDrag)

  // Prevent text selection during drag
  event.preventDefault()
}

const handleDrag = (event: MouseEvent) => {
  if (!dragging.value) return

  position.value = {
    x: event.clientX - dragOffset.value.x,
    y: event.clientY - dragOffset.value.y,
  }
}

const stopDrag = () => {
  dragging.value = false
  document.removeEventListener('mousemove', handleDrag)
  document.removeEventListener('mouseup', stopDrag)
}

// Setup subscription tracking
onMounted(() => {
  // Always get an accurate initial count regardless of expanded state
  refreshCounts()

  // Set up polling to periodically refresh the counts
  refreshIntervalId.value = window.setInterval(() => {
    // Always update totalCount, but only update categories when expanded
    if (expanded.value) {
      refreshCounts()
    }
  }, REFRESH_INTERVAL)

  // Monkey patching for event logging
  const originalRegister = useSubscriptions().registerSubscription

  useSubscriptions().registerSubscription = (unsubscribe, category = 'default', key?: string) => {
    originalRegister(unsubscribe, category, key)
    addEvent('register', category, key)
  }

  useSubscriptions().unregisterSubscription = (unsubscribe) => {
    // We can't easily get the category/key from here since it's removed in the original function
    // So we'll add the unregister event without those details
    unregisterSub(unsubscribe)
    addEvent('unregister', 'unknown')
  }

  // Add keyboard shortcut to toggle panel
  const handleKeyDown = (event: KeyboardEvent) => {
    // Alt+S to toggle the panel
    if (event.altKey && event.code === 'KeyS') {
      enabled.value = !enabled.value
    }
  }

  window.addEventListener('keydown', handleKeyDown)

  onUnmounted(() => {
    window.removeEventListener('keydown', handleKeyDown)
  })
})

// Add clean-up for the interval when component is unmounted
onUnmounted(() => {
  if (refreshIntervalId.value !== null) {
    clearInterval(refreshIntervalId.value)
    refreshIntervalId.value = null
  }
})

// Add a watch on the expanded state to refresh when expanded
watch(expanded, (newValue) => {
  if (newValue) {
    refreshCounts()
  }
})
</script>

<style>
/* Base panel styling */
.subscription-debug-panel {
  position: fixed;
  z-index: 9999;
  width: 300px;
  background-color: rgba(30, 30, 30, 0.9);
  color: #fff;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  font-family: monospace;
  font-size: 12px;
  user-select: none;
  max-height: calc(100vh - 20px);
  display: flex;
  flex-direction: column;
  border: 1px solid #555;
  transition: height 0.2s ease-out;
}

.subscription-debug-panel--expanded {
  min-height: 300px;
}

/* Header styling */
.subscription-debug-panel__header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #333;
  border-bottom: 1px solid #555;
  cursor: move;
}

.subscription-debug-panel__title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: bold;
  color: #ffffff;
}

.subscription-debug-panel__actions {
  display: flex;
  gap: 8px;
}

.subscription-debug-panel__action-button {
  background: none;
  border: none;
  color: #ccc;
  cursor: pointer;
  border-radius: 4px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
}

.subscription-debug-panel__action-button:hover {
  background-color: #444;
  color: #fff;
}

/* Content styling */
.subscription-debug-panel__content {
  display: flex;
  flex-direction: column;
  padding: 10px;
  overflow-y: auto;
  gap: 10px;
  flex: 1;
}

/* Categories */
.subscription-debug-panel__categories {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.subscription-debug-panel__category {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  background-color: #2a2a2a;
  border-radius: 3px;
  border-left: 3px solid var(--kb-accent);
}

.subscription-debug-panel__category-name {
  display: flex;
  align-items: center;
  gap: 6px;
}

.subscription-debug-panel__badge {
  background-color: var(--kb-accent);
  color: var(--kb-accent-ink);
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 10px;
  min-width: 18px;
  text-align: center;
}

.subscription-debug-panel__action-button--small {
  background: none;
  border: none;
  color: #aaa;
  cursor: pointer;
  padding: 2px;
  border-radius: 4px;
  font-size: 10px;
}

.subscription-debug-panel__action-button--small:hover {
  background-color: #444;
  color: #fff;
}

/* Event Log */
.subscription-debug-panel__event-log {
  margin-top: 10px;
  max-height: 200px;
  overflow-y: auto;
  border: 1px solid #444;
  border-radius: 3px;
}

.subscription-debug-panel__section-title {
  padding: 4px 8px;
  background-color: #333;
  font-weight: bold;
  border-bottom: 1px solid #444;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.subscription-debug-panel__events {
  max-height: 150px;
  overflow-y: auto;
}

.subscription-debug-panel__event {
  padding: 4px 8px;
  border-bottom: 1px solid #333;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10px;
}

.subscription-debug-panel__event--register {
  border-left: 3px solid #4caf50;
}

.subscription-debug-panel__event--unregister {
  border-left: 3px solid #f44336;
}

.subscription-debug-panel__event--clear {
  border-left: 3px solid #ff9800;
}

.subscription-debug-panel__event--refresh {
  border-left: 3px solid #2196f3;
}

.subscription-debug-panel__event-time {
  color: #aaa;
}

.subscription-debug-panel__event-type {
  font-weight: bold;
}

.subscription-debug-panel__event-category {
  color: #ddd;
  flex: 1;
}

.subscription-debug-panel__event-key {
  color: #888;
  font-size: 9px;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Subscription Items */
.subscription-debug-panel__subscription-list {
  margin-top: 10px;
  border: 1px solid #444;
  border-radius: 3px;
}

.subscription-debug-panel__filter {
  display: flex;
  align-items: center;
}

.subscription-debug-panel__filter-input {
  background-color: #222;
  border: 1px solid #555;
  color: #fff;
  border-radius: 2px;
  padding: 2px 4px;
  font-size: 10px;
  width: 80px;
  min-height: 16px;
}

.subscription-debug-panel__subscription-items {
  max-height: 150px;
  overflow-y: auto;
}

.subscription-debug-panel__subscription-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 8px;
  border-bottom: 1px solid #333;
  gap: 8px;
}

.subscription-debug-panel__subscription-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  overflow: hidden;
}

.subscription-debug-panel__subscription-category {
  font-weight: bold;
  font-size: 11px;
}

.subscription-debug-panel__subscription-key {
  color: #aaa;
  font-size: 9px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.subscription-debug-panel__subscription-time {
  color: #888;
  font-size: 9px;
}

/* Action buttons */
.subscription-debug-panel__actions-row {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 10px;
}

.subscription-debug-panel__button {
  background-color: #444;
  border: none;
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
}

.subscription-debug-panel__button:hover {
  background-color: #555;
}

.subscription-debug-panel__button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
